import React from 'react'
import {
  faListUl,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useParams } from 'react-router-dom'
import { BoxV4 } from '@labrav/react-components'
import { BOX_COLLAPSIBLE_STYLE_V2 } from '../SubscriptionDetail.styles'
import { renderSubscriptionDetailHeadingIcon } from './renderSubscriptionDetailHeadingIcon'
import { SectionWrapper } from '../../../../common/components/SectionWrapper/SectionWrapper'
import {
  DetailRow,
  DetailInfo,
} from '../../../../common/components/DetailRow/DetailRow'
import { dateAndTimeConversion } from '../../../../common/utils/dateAndTimeConversion'
import { SubscriptionDetailObject } from '../../../../modules/subscription/reducer'
import { ViewValueWithTruncate } from '@labrav/common'
import { TextWithCopyIcon } from '@labrav/common'
import { compactDisplayId } from '../utils/subscriptionDetailHelperImports'
import { productDetailDate } from '../../../../common/utils/productDetailDate'

interface OfferDetailsSectionProps {
  subscriptionDetail: SubscriptionDetailObject
  offerDetailInfo: {
    cloudOfferId?: string
    labraOfferId?: string
    createdAt?: string
  }
  timeZone: string
  classes: any
  cloudType?: string
}

const renderDateCell = (dateStr: string, timeZone: string, classes: any) => {
  const { modifiedDate, modifiedTime } = productDetailDate(dateStr, timeZone)
  return (
    <div className={classes.dateTimeContainer}>
      <div className={classes.createdAtDate}>{modifiedDate}</div>
      {modifiedTime && (
        <div className={classes.createdAtTime}>{modifiedTime.trim()}</div>
      )}
    </div>
  )
}

export const OfferDetailsSection: React.FC<OfferDetailsSectionProps> = ({
  subscriptionDetail,
  offerDetailInfo,
  timeZone,
  classes,
  cloudType,
}) => {
  const navigate = useNavigate()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()

  if (cloudType === 'GCP' && subscriptionDetail) {
    const offerDetails = subscriptionDetail.offerDetails
    if (!offerDetails) return null

    const offerPath =
      offerDetails.labraProductId && offerDetails.labraOfferId
        ? `/flyout/${routeCloud || 'gcp'}/${routeAccountId}/products-listing/${offerDetails.labraProductId}/private-offers/${offerDetails.labraOfferId}/details`
        : null

    return (
      <div className={classes.boxWrapper}>
        <BoxV4
          variant="small"
          headingVariant="small"
          heading="Offer details"
          headingIcon={renderSubscriptionDetailHeadingIcon(classes)}
          collapsible
          collapsibleStyle={BOX_COLLAPSIBLE_STYLE_V2}
        >
          <div className={classes.sectionContentV2}>
            <DetailRow borderBottom props={{ spacing: 1 }}>
              <>
                <DetailInfo
                  title="Offer name"
                  value={
                    <div className={classes.productValue}>
                      <span>{offerDetails.offerName || '-'}</span>
                      {offerPath && (
                        <FontAwesomeIcon
                          icon={faArrowUpRightFromSquare}
                          className={classes.productLinkIcon}
                          onClick={() => navigate(offerPath)}
                          title="View offer in platform"
                        />
                      )}
                    </div>
                  }
                  keyName="offerName"
                />
              </>
            </DetailRow>
            <DetailRow borderBottom props={{ spacing: 1 }}>
              <>
                <DetailInfo
                  title="Cloud offer ID"
                  value={
                    offerDetails.cloudOfferId ? (
                      <TextWithCopyIcon
                        text={offerDetails.cloudOfferId}
                        displayText={compactDisplayId(
                          offerDetails.cloudOfferId
                        )}
                      />
                    ) : (
                      '-'
                    )
                  }
                  keyName="cloudOfferId"
                />
                <DetailInfo
                  title="Labra offer ID"
                  value={
                    offerDetails.labraOfferId ? (
                      <TextWithCopyIcon
                        text={offerDetails.labraOfferId}
                        displayText={compactDisplayId(
                          offerDetails.labraOfferId
                        )}
                      />
                    ) : (
                      '-'
                    )
                  }
                  keyName="labraOfferId"
                />
              </>
            </DetailRow>
            <DetailRow borderBottom={false} props={{ spacing: 1 }}>
              <>
                <DetailInfo
                  title="Created on"
                  value={
                    offerDetails.createdOn
                      ? renderDateCell(
                          offerDetails.createdOn,
                          timeZone,
                          classes
                        )
                      : '-'
                  }
                  keyName="offerCreatedOn"
                />
                <DetailInfo
                  title="Accepted on"
                  value={
                    offerDetails.acceptedOn
                      ? renderDateCell(
                          offerDetails.acceptedOn,
                          timeZone,
                          classes
                        )
                      : '-'
                  }
                  keyName="acceptedOn"
                />
              </>
            </DetailRow>
          </div>
        </BoxV4>
      </div>
    )
  }

  return (
    <SectionWrapper heading="Offer details" icon={faListUl}>
      <div className={classes.sectionContent}>
        <DetailRow borderBottom props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Cloud offer ID"
              value={
                <ViewValueWithTruncate
                  value={offerDetailInfo?.cloudOfferId}
                  showIcon={true}
                />
              }
              keyName="cloudOfferId"
            />
            <DetailInfo
              title="Labra offer ID"
              value={
                <ViewValueWithTruncate
                  value={offerDetailInfo?.labraOfferId}
                  showIcon={true}
                />
              }
              keyName="labraOfferId"
            />
          </>
        </DetailRow>
        <DetailRow borderBottom={false} props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Created on"
              value={
                offerDetailInfo.createdAt
                  ? (() => {
                      const { modifiedDate, modifiedTime } =
                        dateAndTimeConversion(
                          offerDetailInfo.createdAt,
                          timeZone
                        )
                      return (
                        <div
                          style={{
                            display: 'flex',
                            gap: 4,
                            alignItems: 'center',
                          }}
                        >
                          <div className={classes.createdAtDate}>
                            {modifiedDate}
                          </div>
                          {modifiedTime && (
                            <div className={classes.createdAtTime}>
                              {modifiedTime.trim()}
                            </div>
                          )}
                        </div>
                      )
                    })()
                  : '-'
              }
              keyName="offerCreatedOn"
            />
            <DetailInfo
              title="Accepted on"
              value={
                offerDetailInfo.createdAt
                  ? (() => {
                      const { modifiedDate, modifiedTime } =
                        dateAndTimeConversion(
                          offerDetailInfo.createdAt,
                          timeZone
                        )
                      return (
                        <div
                          style={{
                            display: 'flex',
                            gap: 4,
                            alignItems: 'center',
                          }}
                        >
                          <div className={classes.createdAtDate}>
                            {modifiedDate}
                          </div>
                          {modifiedTime && (
                            <div className={classes.createdAtTime}>
                              {modifiedTime.trim()}
                            </div>
                          )}
                        </div>
                      )
                    })()
                  : '-'
              }
              keyName="acceptedOn"
            />
          </>
        </DetailRow>
      </div>
    </SectionWrapper>
  )
}
